import {
  Users, UserPlus, Layers, CalendarRange, GraduationCap,
  CalendarX, BookX, TrendingDown,
} from 'lucide-react';
import { officialPeriodStart, officialPeriodEnd } from '../../utils/dateRange';
import { formatDate } from '../../utils/dateFormat';

/**
 * The rule vocabulary of a session's expected-attendee list, as the browser
 * sees it (docs/modules/ld.md §3.2). The server's
 * services/audienceRules.js is the authority on validation and SQL; this
 * file mirrors its shapes for the editor, the picker modal, the History
 * timeline and the session detail panel.
 *
 * Rules add up (union). The intersection lives inside a rule: `profile` ANDs
 * its department / grade / designation lists, and every record-based rule may
 * carry the same three lists as `where` to narrow itself.
 */

export const EMPTY_AUDIENCE_RULES = { include: [], exclude: { staffIds: [] } };

export const PROFILE_KEYS = ['departments', 'grades', 'designations'];
export const emptyProfile = () => ({ departments: [], grades: [], designations: [] });
export const profileIsEmpty = (p) => !p || PROFILE_KEYS.every((k) => !p[k] || p[k].length === 0);

/** "Department: Audit · Grade: Senior" — same wording as the server's label. */
export const profileLabel = (p) => [
  p?.departments?.length ? `Department: ${p.departments.join(', ')}` : null,
  p?.grades?.length ? `Grade: ${p.grades.join(', ')}` : null,
  p?.designations?.length ? `Designation: ${p.designations.join(', ')}` : null,
].filter(Boolean).join(' · ');

// The three dated rules open on the current training year, read from the L&D
// settings at add time — a literal would freeze whatever the settings said
// when this module was first imported. Clearing a box still means "no bound".
const datedBlank = (rest) => () => ({
  ...rest, from: officialPeriodStart(), to: officialPeriodEnd(), where: emptyProfile(),
});

/**
 * type → how the rule is offered and judged.
 *   label / desc / icon — the Add menu card and the rule's own heading
 *   group               — which Add-menu section it sits in
 *   single              — a second rule of this type could only repeat the
 *                         first, so the menu offers it once
 *   narrowable          — may carry `where` (department / grade / designation)
 *   blank()             — a fresh rule of this type
 *   incomplete(rule)    — true while the rule cannot be saved
 *   todo                — what is still missing, named as the field
 */
export const RULE_META = {
  all_active: {
    label: 'Everyone (All Active Staff)', group: 'profile', icon: Users, single: true,
    desc: 'Every active staff member, no conditions.',
    blank: () => ({ type: 'all_active' }),
    incomplete: () => false,
  },
  profile: {
    label: 'Department, Grade or Designation', group: 'profile', icon: Layers,
    desc: 'Tick departments, grades and designations.',
    // Behind the help icon, not in the running text.
    tip: 'Ticks in different columns combine, so "Audit" plus "Senior" is the Audit Seniors. Ticks in the same column are alternatives. A column with nothing ticked is not a condition.',
    blank: () => ({ type: 'profile', ...emptyProfile() }),
    incomplete: (r) => profileIsEmpty(r),
    todo: 'Tick at least one department, grade or designation to finish this rule.',
  },
  joined_between: {
    label: 'Joined Between', group: 'profile', icon: CalendarRange, narrowable: true,
    desc: 'Hired within a date range, e.g. this year’s new joiners.',
    blank: datedBlank({ type: 'joined_between' }),
    incomplete: (r) => !r.from && !r.to,
    todo: 'Set at least one end of the date range to finish this rule.',
  },
  qualification: {
    label: 'Professional Qualification', group: 'profile', icon: GraduationCap, narrowable: true,
    desc: 'People who hold, or do not hold, a qualification (CA, ACCA…).',
    blank: () => ({ type: 'qualification', typeIds: [], held: true, where: emptyProfile() }),
    incomplete: (r) => r.typeIds.length === 0,
    todo: 'Tick at least one qualification to finish this rule.',
  },
  individual: {
    label: 'Named Individuals', group: 'profile', icon: UserPlus, single: true,
    desc: 'Pick specific people by name.',
    blank: () => ({ type: 'individual', staffIds: [] }),
    incomplete: (r) => r.staffIds.length === 0,
    todo: 'Add at least one person to finish this rule.',
  },
  missed_session: {
    label: 'Missed a Past Session', group: 'history', icon: CalendarX, narrowable: true,
    desc: 'Anyone who did not attend an earlier session, for a catch-up run.',
    blank: () => ({ type: 'missed_session', sessionIds: [], mode: 'no_show', where: emptyProfile() }),
    incomplete: (r) => r.sessionIds.length === 0,
    todo: 'Tick at least one past session to finish this rule.',
  },
  missing_mandatory: {
    label: 'Has Not Taken Mandatory Training in a Category', group: 'history', icon: BookX, narrowable: true,
    desc: 'No mandatory session of a category attended yet. Pick the category and the period.',
    blank: datedBlank({ type: 'missing_mandatory', courseId: '' }),
    incomplete: (r) => !r.courseId,
    todo: 'Pick a category to finish this rule.',
  },
  below_target: {
    label: 'Below the Mandatory-Hours Target', group: 'history', icon: TrendingDown, narrowable: true,
    desc: 'Behind on mandatory hours for the period, e.g. under 40 h this year.',
    blank: datedBlank({ type: 'below_target', hours: '' }),
    incomplete: (r) => !(Number(r.hours) > 0),
    todo: 'Enter the hours threshold to finish this rule.',
  },
};

export const RULE_ORDER = Object.keys(RULE_META);

// Each group heading carries a hint saying which staff field or record it
// reads; a bare category name ("Who They Are") reads as a riddle at pick time.
export const RULE_GROUPS = [
  {
    key: 'profile',
    label: 'Who They Are',
    hint: 'Read from the staff record: department, grade, designation, qualification, join date, or picked by name.',
  },
  {
    key: 'history',
    label: 'What They Have (Not) Done',
    hint: 'Read from their training record: sessions missed, mandatory categories still owed, hours still short.',
  },
];

/**
 * One stored rule → the shape the editor works on. Sessions saved before the
 * combined profile rule existed carry single-list `department` / `grade` /
 * `designation` rules; they open as a profile rule with that one column ticked.
 * Narrowable rules always carry the three `where` lists so the picker can bind
 * to them.
 */
export const normalizeRule = (r) => {
  if (!r || typeof r !== 'object') return r;
  switch (r.type) {
    case 'department': return { type: 'profile', ...emptyProfile(), departments: [...(r.values || [])] };
    case 'grade': return { type: 'profile', ...emptyProfile(), grades: [...(r.values || [])] };
    case 'designation': return { type: 'profile', ...emptyProfile(), designations: [...(r.values || [])] };
    case 'profile': return { ...emptyProfile(), ...r };
    default:
      if (RULE_META[r.type]?.narrowable) {
        return { ...r, where: { ...emptyProfile(), ...(r.where || {}) } };
      }
      return r;
  }
};

export const normalizeRules = (value) => ({
  include: Array.isArray(value?.include) ? value.include.map(normalizeRule) : [],
  exclude: { staffIds: value?.exclude?.staffIds || [] },
});

export const isRuleIncomplete = (rule) => RULE_META[rule.type]?.incomplete(rule) ?? false;

/** Only the ticked lists go on the wire; blank ones are not a condition. */
const compactProfile = (p) => {
  const out = {};
  PROFILE_KEYS.forEach((k) => { if (p?.[k]?.length) out[k] = p[k]; });
  return out;
};

/** What the API accepts for one complete rule: numbers where numbers belong. */
export const rulePayload = (raw) => {
  const r = normalizeRule(raw);
  let out;
  if (r.type === 'profile') {
    out = { type: 'profile', ...compactProfile(r) };
  } else if (r.type === 'missing_mandatory') {
    out = { ...r, courseId: Number(r.courseId), from: r.from || null, to: r.to || null };
  } else if (r.type === 'below_target') {
    out = { ...r, hours: Number(r.hours), from: r.from || null, to: r.to || null };
  } else if (r.type === 'joined_between') {
    out = { ...r, from: r.from || null, to: r.to || null };
  } else {
    out = { ...r };
  }
  if (RULE_META[r.type]?.narrowable) {
    const where = compactProfile(r.where);
    if (Object.keys(where).length > 0) out.where = where; else delete out.where;
  }
  return out;
};

/** What the API accepts: complete rules only. */
export const audienceRulesPayload = (value) => {
  const rules = normalizeRules(value);
  return {
    include: rules.include.filter((r) => !isRuleIncomplete(r)).map(rulePayload),
    exclude: { staffIds: rules.exclude.staffIds },
  };
};

const fmtRange = (from, to) => {
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  if (from) return `from ${formatDate(from)}`;
  if (to) return `up to ${formatDate(to)}`;
  return 'any date';
};

/**
 * One rule → one phrase, worded like the server's roster label. `lookups`
 * ({ sessions: {id: {courseTitle, date}}, categories: {id: title},
 * qualificationTypes: {id: title}, staff: {id: name} }) turns ids into names
 * where the caller has them; without it the phrase falls back to counts and
 * ids. Single source of the wording for the editor's rule rows, the History
 * timeline and the session detail panel.
 */
export const describeRule = (raw, lookups = {}) => {
  const r = normalizeRule(raw);
  let base;
  switch (r.type) {
    case 'profile': base = profileLabel(r); break;
    case 'all_active': base = 'All active staff'; break;
    case 'individual': {
      const names = r.staffIds.map((id) => lookups.staff?.[id]).filter(Boolean);
      base = names.length === r.staffIds.length && names.length <= 3
        ? `Named: ${names.join(', ')}`
        : `${r.staffIds.length} named ${r.staffIds.length === 1 ? 'person' : 'people'}`;
      break;
    }
    case 'joined_between': base = `Joined ${fmtRange(r.from, r.to)}`; break;
    case 'qualification': {
      const names = r.typeIds.map((id) => lookups.qualificationTypes?.[id] || `#${id}`);
      base = `${r.held ? 'Holds' : 'Without'}: ${names.join(', ')}`;
      break;
    }
    case 'missed_session': {
      const verb = r.mode === 'excused' ? 'Excused from' : r.mode === 'any' ? 'Did not attend' : 'Missed';
      const s = r.sessionIds.length === 1 ? lookups.sessions?.[r.sessionIds[0]] : null;
      base = s
        ? `${verb}: ${s.courseTitle} — ${formatDate(s.date)}`
        : `${verb} ${r.sessionIds.length} past ${r.sessionIds.length === 1 ? 'session' : 'sessions'}`;
      break;
    }
    case 'missing_mandatory': {
      const title = lookups.categories?.[r.courseId] || `#${r.courseId}`;
      base = `Not yet taken: ${title} (${fmtRange(r.from, r.to)})`;
      break;
    }
    case 'below_target': base = `Below ${r.hours} h mandatory (${fmtRange(r.from, r.to)})`; break;
    default: base = r.type;
  }
  const narrowed = RULE_META[r.type]?.narrowable && !profileIsEmpty(r.where)
    ? ` · ${profileLabel(r.where)}` : '';
  return `${base}${narrowed}`;
};

/** Every rule as its own phrase, in the order they were added. */
export const describeAudienceRules = (rules, lookups) => (
  Array.isArray(rules?.include) ? rules.include.map((r) => describeRule(r, lookups)) : []
);

/** One line for a whole rule set: the History timeline's before/after cells. */
export const summariseAudienceRules = (rules, lookups) => {
  if (!rules || !Array.isArray(rules.include)) return '—';
  const parts = describeAudienceRules(rules, lookups);
  const removed = rules.exclude?.staffIds?.length || 0;
  if (removed > 0) parts.push(`${removed} removed`);
  return parts.length > 0 ? parts.join(' | ') : 'Nobody';
};
